// Post status values matching the backend
export type PostStatus =
  | 'draft'
  | 'pending_review'
  | 'approved'
  | 'published'
  | 'scheduled'
  | 'failed'

export type ContentType =
  | 'thought_leadership'
  | 'pain_point'
  | 'social_proof'
  | 'case_study'
  | 'cta'

export type ContentTone =
  | 'professional'
  | 'conversational'
  | 'bold'
  | 'inspirational'

export type Post = {
  readonly id: string
  readonly content: string
  readonly status: PostStatus
  readonly topic?: string
  readonly content_type?: ContentType
  readonly tone?: ContentTone
  readonly hashtags?: readonly string[]
  readonly scheduled_for?: string
  readonly published_at?: string
  readonly created_at: string
  readonly updated_at?: string
  /** Legacy aliases; the API returns the metrics_* columns below. */
  readonly likes?: number
  readonly comments?: number
  readonly impressions?: number
  readonly metrics_likes?: number
  readonly metrics_comments?: number
  readonly metrics_impressions?: number
  readonly guardrail_warnings?: readonly string[]
  /** Image attached to the post; publishes to LinkedIn with the text. */
  readonly media_url?: string | null
  /** 'auto' when the recurring auto-poster wrote it; 'manual' otherwise. */
  readonly origin?: 'manual' | 'auto'
  /** Auto-post campaign that wrote/owns it. */
  readonly schedule_id?: string | null
  /** Comment keyword that triggers a DM (Pro, Comment-to-DM). */
  readonly dm_keyword?: string | null
  readonly dm_template?: string | null
  /** Why the last publish attempt failed. */
  readonly publish_error?: string | null
}

export type CalendarEntry = {
  readonly date: string // ISO date string (YYYY-MM-DD)
  readonly posts: readonly Post[]
}

export type PostFilters = {
  readonly status?: PostStatus | 'all'
  readonly sort?: 'newest' | 'oldest' | 'scheduled'
}

export type GeneratePostRequest = {
  readonly topic: string
  readonly content_type: ContentType
  readonly tone: ContentTone
}

export type ContentStats = {
  readonly total_posts: number
  readonly published_this_week: number
  readonly pending_review: number
  readonly upcoming_scheduled: number
  readonly failed?: number
}

export const STATUS_LABELS: Record<PostStatus, string> = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  approved: 'Approved',
  published: 'Published',
  scheduled: 'Scheduled',
  failed: 'Publish Failed',
}

export const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  thought_leadership: 'Thought Leadership',
  pain_point: 'Pain Point',
  social_proof: 'Social Proof',
  case_study: 'Case Study',
  cta: 'CTA',
}

export const TONE_LABELS: Record<ContentTone, string> = {
  professional: 'Professional',
  conversational: 'Conversational',
  bold: 'Bold',
  inspirational: 'Inspirational',
}
